import { useEffect, useState } from "react";
import {
  buildTrackingPayload,
  captureVisit,
  emptyTrackingPayload,
  mergeAttribution,
  type StoredAttribution,
  type TrackingPayload,
} from "@shared/tracking";
import { getConsent, onConsentChange } from "@/lib/consent";

const STORAGE_KEY = "aspab-attribution";

/**
 * Attribution for this page view, kept in memory only. Before the visitor has
 * made a cookie choice nothing is written to the device; if they accept, this
 * is folded into localStorage so the ad click that brought them is not lost.
 */
let sessionAttribution: StoredAttribution = {};
let consentListenerAttached = false;

function readStored(): StoredAttribution {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeStored(value: StoredAttribution): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage blocked (private mode, disabled site data) — the form still works.
  }
}

function clearStored(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/**
 * Records utm parameters and click ids (gclid, gbraid, wbraid, fbclid) from the
 * current URL. Called on every page load. Persisted in localStorage only after
 * the visitor accepts cookies; removed if they decline.
 */
export function captureAttribution(): void {
  if (typeof window === "undefined") return;

  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return;
  }
  const now = new Date();
  sessionAttribution = captureVisit(sessionAttribution, url, now);

  const consent = getConsent();
  if (consent === "accepted") {
    writeStored(captureVisit(readStored(), url, now));
  } else if (consent === "declined") {
    clearStored();
  }

  if (!consentListenerAttached) {
    consentListenerAttached = true;
    onConsentChange((status) => {
      if (status === "accepted") {
        writeStored(mergeAttribution(readStored(), sessionAttribution));
      } else if (status === "declined") {
        clearStored();
      }
    });
  }
}

/**
 * Current tracking fields, computed at the moment of the call.
 * Accepted: stored history. Not chosen yet: this visit only. Declined: empty.
 */
export function readTrackingPayload(): TrackingPayload {
  if (typeof window === "undefined") return emptyTrackingPayload();
  const consent = getConsent();
  if (consent === "declined") return emptyTrackingPayload();
  const source = consent === "accepted" ? readStored() : sessionAttribution;
  return buildTrackingPayload(source, new Date());
}

/** Tracking fields for hidden form inputs; empty during SSR. */
export function useUtmParams(): TrackingPayload {
  const [payload, setPayload] = useState<TrackingPayload>(emptyTrackingPayload);

  useEffect(() => {
    setPayload(readTrackingPayload());
    return onConsentChange(() => setPayload(readTrackingPayload()));
  }, []);

  return payload;
}
